import { AssessmentDomainStatus, ClassificationConfidence, DocumentClassificationInvalidReason, DocumentClassificationStatus, FieldTypeEnum, FileStatus } from '@libs/constants';
import {
  AssessmentDetail,
  AssessmentDomainDetail,
  AssessmentDomainDocumentDetail,
  AssessmentResultsMeta,
  DomainDocumentComparisonChangeType,
  DomainResultEntry,
  PreviousAssessmentSummary,
  PreviousDomainSummary,
} from '@libs/types';
import { getMaturityByValue, isBlockingClassificationInvalidReason } from '@libs/utils';

import {
  AssessmentDocumentViewModel,
  AssessmentOverviewViewModel,
  DomainResultViewModel,
  OverallResultsViewModel,
} from '../types';

// Progress flow item type (local to avoid cross-import issues)
export interface ProgressFlowItem {
  id: string;
  title: string;
  isOptional?: boolean;
}



export interface ProgressFlowViewModel {
  assessmentItems: ProgressFlowItem[];
  domainStates: Map<number, AssessmentDomainStatus>;
  domainOptionalMap: Map<number, boolean>;
  /**
   * Per-domain `isModifiedFromPrevious` flag, keyed by 1-based orderIndex.
   * Drives the purple "edited" pin overlay on each circle when the
   * assessment is a re-assessment (version > 1).
   */
  domainModifiedMap: Map<number, boolean>;
  currentDomainIndex: number;
  totalDomains: number;
  completionPercentage: number;
}

export interface DomainDetailsViewModel {
  id: string;
  maxRequiredDocuments: number;
  minRequiredDocuments: number; // Minimum required documents count for validation
  status: AssessmentDomainStatus;
  isApplicable: boolean;
  isSkipped: boolean;
  isRequired: boolean;
  domainName: string;
  description?: string | null;
  orderIndex: number;
  fields: Array<{
    type: FieldTypeEnum;
    id: string;
    name: string;
    description?: string | null;
    required?: boolean;
    orderIndex: number;
    documentRequirements?: Array<{
      id: string;
      key: string;
      name: string;
      description?: string;
      acceptedFormats: string[];
      standardFormat?: string[] | null;
      maxSizeBytes: number;
      standardDocument?: {
        id: string;
        name: string;
        description?: string | null;
        key: string;
        documentType?: string | null;
      };
    }>;
  }>;
}

export type ExpectedDocumentStatus = 'matched' | 'missing' | 'wrong_format';

export interface ExpectedDocument {
  name: string;
  description: string;
  required?: boolean;
  status: ExpectedDocumentStatus;
  matchedFileName?: string;
  confidence?: ClassificationConfidence;
  /**
   * Format the standard version of this document is produced as (e.g.
   * `['.docx']`). Null when the seeded catalog carried no standard_format.
   */
  standardFormat?: string[] | null;
}

export const extractExpectedDocuments = (
  fields: DomainDetailsViewModel['fields'],
  documents: AssessmentDomainDocumentDetail[] = [],
): ExpectedDocument[] => {
  // Map expected doc name → first non-deleted completed match
  const matchMap = new Map<string, { fileName: string; confidence: ClassificationConfidence | null; needsReview: boolean; wrongFormat: boolean }>();
  for (const doc of documents) {
    const meta = doc.classificationMetadata;
    if (doc.deleted || !meta || meta.classificationStatus !== DocumentClassificationStatus.COMPLETED || !meta.matchedStandardDocumentName) continue;
    if (!matchMap.has(meta.matchedStandardDocumentName)) {
      matchMap.set(meta.matchedStandardDocumentName, {
        fileName: doc.fileName,
        confidence: meta.classificationConfidence,
        // WRONG_FORMAT is advisory: the document matched correctly, so it must
        // not fall back into "needs review" — it gets its own status instead.
        needsReview:
          isBlockingClassificationInvalidReason(meta.invalidReason) ||
          meta.classificationConfidence === ClassificationConfidence.LOW,
        wrongFormat: meta.invalidReason === DocumentClassificationInvalidReason.WRONG_FORMAT,
      });
    }
  }

  const seen = new Map<string, { description: string; required?: boolean; standardFormat?: string[] | null }>();
  fields.forEach((field) => {
    field.documentRequirements?.forEach((req) => {
      const name = req.standardDocument?.name || req.name;
      if (name && !seen.has(name)) {
        seen.set(name, {
          description: req.standardDocument?.description || req.description || '',
          required: field.required,
          standardFormat: req.standardFormat ?? null,
        });
      }
    });
  });

  return Array.from(seen.entries()).map(([name, { description, required, standardFormat }]) => {
    const match = matchMap.get(name);
    let status: ExpectedDocumentStatus = 'missing';
    if (match) {
      if (match.needsReview) status = 'missing';
      else if (match.wrongFormat) status = 'wrong_format';
      else status = 'matched';
    }
    return {
      name,
      description,
      required,
      status,
      matchedFileName: match?.fileName,
      confidence: match?.confidence ?? undefined,
      standardFormat,
    };
  });
};

export interface AssessmentHeaderViewModel {
  assessmentName: string;
  assessmentDate: string;
}

/**
 * Re-assessment context derived from `previousVersion` plus the current
 * domain's documents. Lives here so the page component never has to reach
 * into the raw API response. All fields are nullable/undefined to express
 * the v1 (no parent) case without forcing callers to null-check the wrapper.
 */
export interface PreviousVersionViewModel {
  /** Parent assessment version, or `undefined` for v1 (no parent). */
  parentVersion: number | undefined;
  /** Parent submission timestamp, or `null` when missing. */
  parentSubmittedAt: string | null;
  /** Parent maturity percentage for THIS domain, or `null` if unscored. */
  parentDomainScorePercentage: number | null;
  /**
   * Documents carried over from the parent's version of THIS domain that are
   * still on the domain — i.e. what the banner means by "pre-loaded". Drops as
   * the user removes carry-overs and is 0 once they have removed them all.
   * Documents uploaded fresh in this version are not counted. `undefined` for v1.
   */
  preloadedDocumentCount: number | undefined;
}

/**
 * Maps assessment domain details response to ProgressFlow component format
 * Uses assessment.domains array which should include frameworkDomain info when includeAssessment=true
 */
export const mapToProgressFlowViewModel = (
  assessment: AssessmentDetail | undefined,
): ProgressFlowViewModel => {
  if (!assessment?.assessmentDomains) {
    return {
      assessmentItems: [],
      domainStates: new Map(),
      domainOptionalMap: new Map(),
      domainModifiedMap: new Map(),
      currentDomainIndex: -1,
      totalDomains: 0,
      completionPercentage: 0,
    };
  }

  const domains = assessment.assessmentDomains as AssessmentDomainDetail[];

  const sortedDomains = [...domains].sort((a, b) => {
    const aOrder = a.frameworkDomain?.orderIndex ?? 0;
    const bOrder = b.frameworkDomain?.orderIndex ?? 0;
    return aOrder - bOrder;
  });

  const items: ProgressFlowItem[] = sortedDomains.map((domain) => ({
    id: domain.id,
    // Display remains 1-based from API
    title: domain.frameworkDomain?.name ?? '',
    // Domain is optional only if frameworkDomain.required is false
    // This is determined by the framework definition, not by user actions (isSkipped/isApplicable)
    isOptional: domain.frameworkDomain?.required === false,
  }));

  const states = new Map<number, AssessmentDomainStatus>();
  const optionalMap = new Map<number, boolean>();
  const modifiedMap = new Map<number, boolean>();
  let completedCount = 0;

  sortedDomains.forEach((domain, idx) => {
    const apiOrderIndex = domain.frameworkDomain?.orderIndex ?? idx + 1; // API is 1-based
    states.set(apiOrderIndex, domain.status);
    // Domain is optional only if frameworkDomain.required is false
    // This is determined by the framework definition, not by user actions (isSkipped/isApplicable)
    optionalMap.set(apiOrderIndex, domain.frameworkDomain?.required === false);
    modifiedMap.set(apiOrderIndex, domain.isModifiedFromPrevious === true);

    if (domain.status === AssessmentDomainStatus.COMPLETED) {
      completedCount++;
    }
  });

  const total = sortedDomains.length;
  const percentage =
    assessment.progress || (total > 0 ? Math.round((completedCount / total) * 100) : 0);

  // Active domain is the one marked IN_PROGRESS (API guarantees single IN_PROGRESS)
  const inProgressDomain = sortedDomains.find(
    (domain) => domain.status === AssessmentDomainStatus.IN_PROGRESS,
  );
  const currentIndex = inProgressDomain?.frameworkDomain?.orderIndex ?? 1;

  return {
    assessmentItems: items,
    domainStates: states,
    domainOptionalMap: optionalMap,
    domainModifiedMap: modifiedMap,
    currentDomainIndex: currentIndex,
    totalDomains: total,
    completionPercentage: percentage,
  };
};

/**
 * Finds the orderIndex for a given domain ID
 * @param assessment - The assessment data containing all domains
 * @param domainId - The domain ID to find
 * @returns The orderIndex (1-based from API) if found, 0 otherwise
 */
export const findDomainById = (
  assessment: AssessmentDetail | undefined,
  domainId: string,
): AssessmentDomainDetail | undefined => {
  if (!assessment?.assessmentDomains) {
    return undefined;
  }

  const domains = assessment.assessmentDomains as AssessmentDomainDetail[];
  const domain = domains.find((d) => d.id === domainId);
  return domain;
};

/**
 * Finds the domain for a given orderIndex
 * @param assessment - The assessment data containing all domains
 * @param orderIndex - The orderIndex to find (1-based from API)
 * @returns The domain if found, undefined otherwise
 */
export const findDomainByOrderIndex = (
  assessment: AssessmentDetail | undefined,
  orderIndex: number,
): AssessmentDomainDetail | undefined => {
  if (!assessment?.assessmentDomains) {
    return undefined;
  }

  const domains = assessment.assessmentDomains as AssessmentDomainDetail[];
  const domain = domains.find(
    (d) => d.frameworkDomain?.orderIndex === orderIndex,
  );
  return domain;
};

/**
 * Maps assessment domain details response to DomainDetails component format
 */
export const mapToDomainDetailsViewModel = (
  assessmentDomain: AssessmentDomainDetail,
): DomainDetailsViewModel | null => {

  if (!assessmentDomain?.frameworkDomain) {
    return null;
  }

  const frameworkDomain = assessmentDomain.frameworkDomain;
  const fields = (frameworkDomain.fields || []).map((field) => ({
    type: FieldTypeEnum.DOCUMENT,
    id: field.id,
    name: field.name,
    description: field.description,
    required: true, // Can be determined from documentRequirements
    orderIndex: field.orderIndex,
    documentRequirements: (field.documentRequirements || []).map((req) => ({
      id: req.id,
      key: req.standardDocument?.key ?? '',
      name: req.standardDocument?.name ?? '',
      description: req.standardDocument?.description ?? '',
      acceptedFormats: req.acceptedFormats,
      standardFormat: req.standardFormat ?? null,
      maxSizeBytes: req.maxSizeBytes,
      standardDocument: req.standardDocument ? {
        id: req.standardDocument.id,
        name: req.standardDocument.name,
        description: req.standardDocument.description,
        key: req.standardDocument.key,
        documentType: req.standardDocument.documentType,
      } : undefined,
    })),
  }));

  // Calculate minimum required documents from all fields' document requirements
  const maxRequiredDocuments = fields.reduce((total, field) => {
    return total + (field.documentRequirements?.length || 0);
  }, 0);

  return {
    id: assessmentDomain.id,
    maxRequiredDocuments,
    minRequiredDocuments: 1,
    status: assessmentDomain.status,
    isApplicable: assessmentDomain.isApplicable,
    isSkipped: assessmentDomain.isSkipped,
    isRequired: frameworkDomain.required,
    domainName: frameworkDomain.name,
    description: frameworkDomain.description,
    orderIndex: frameworkDomain.orderIndex,
    fields,
  };
};

/**
 * Maps assessment data to header component format
 */
export const mapToAssessmentHeaderViewModel = (
  assessment: AssessmentDetail | undefined,
): AssessmentHeaderViewModel => {
  if (!assessment) {
    return {
      assessmentName: 'Assessment',
      assessmentDate: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    };
  }

  const date = new Date(assessment.createdAt);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return {
    assessmentName: assessment.title,
    assessmentDate: formattedDate,
  };
};

/**
 * Maps the two parent-version summaries + current domain documents into the
 * re-assessment view model consumed by the workflow page. Centralises the
 * parent-version derivations (version, submittedAt, score, doc count) so the
 * page component does not reach into the raw API shape.
 *
 * Both summaries are independently optional — only emitted when their flag
 * was passed AND a parent exists. The view model keeps a single flat shape
 * regardless, so the page does not need to compose two sources by hand.
 */
export const mapToPreviousVersionViewModel = (
  previousAssessmentSummary: PreviousAssessmentSummary | undefined,
  previousDomainSummary: PreviousDomainSummary | undefined,
  currentDomainDocuments: AssessmentDomainDocumentDetail[] | undefined,
): PreviousVersionViewModel => {
  let preloadedDocumentCount: number | undefined;
  if (previousDomainSummary) {
    // A carry-over stops being "pre-loaded" the moment the user removes it, so
    // the banner counts only the carry-overs still on the domain.
    //
    // Both filters matter. `changeType === Unchanged` is what marks a row as a
    // carry-over (the API tags a row `Added` when its file is new to this
    // version), and `!deleted` is what keeps a removed one from still counting:
    // deleting in the workflow is a soft delete, so the row survives in the
    // response until the domain is saved or discarded. Missing the `deleted`
    // check — while `previousDomainSummary.removedDocuments` was also added in
    // — is what made the count climb by one per deletion instead of falling.
    preloadedDocumentCount = (currentDomainDocuments ?? []).filter(
      (d) => !d.deleted && d.changeType === DomainDocumentComparisonChangeType.Unchanged,
    ).length;
  }

  return {
    parentVersion: previousAssessmentSummary?.assessment.version,
    parentSubmittedAt: previousAssessmentSummary?.assessment.submittedAt ?? null,
    parentDomainScorePercentage: previousDomainSummary?.results.maturity.percentage ?? null,
    preloadedDocumentCount,
  };
};

/**
 * Maps assessment domain documents to FileState format
 * Combines UI workflow state with backend document data
 *
 * Status mapping:
 * - deleted → FileStatus.DELETED
 * - confirmed → FileStatus.UPLOADED (saved with domain)
 * - pending → FileStatus.NOT_SAVED (uploaded to S3, not saved yet)
 * - default → FileStatus.QUEUED (not uploaded)
 *
 * Note: FileStatus.UPLOADING is managed by optimistic UI during S3 upload
 */
export const mapToDocumentFilesViewModel = (
  documents: AssessmentDomainDocumentDetail[] = [],
): AssessmentDocumentViewModel[] => {
  return documents.map((doc) => ({
    // UI workflow fields
    id: doc.documentId, // Use documentId for deduplication with uploading files
    fileName: doc.fileName,
    size: doc.fileSize,
    status: doc.deleted
      ? FileStatus.DELETED
      : doc.status === 'confirmed'
        ? FileStatus.UPLOADED
        : doc.status === 'pending'
          ? FileStatus.NOT_SAVED
          : FileStatus.QUEUED,
    progress: doc.status === 'confirmed' || doc.status === 'pending' ? 100 : 0,

    // Backend document fields
    documentId: doc.documentId,
    assessmentDomainDocumentId: doc.id,
    s3Key: doc.s3Key,
    contentType: doc.contentType,
    uploadedById: doc.uploadedById,
    uploadedAt: doc.uploadedAt,
    documentStatus: doc.status,
    deleted: doc.deleted,
    classificationMetadata: doc.classificationMetadata,
    standardDocument: doc.standardDocument,
    changeType: doc.changeType,
  }));
};



/**
 * Calculates total number of files attached across all domains in an assessment
 * Counts only confirmed documents (deleted documents are excluded)
 */
export const calculateTotalFilesCount = (assessment: AssessmentDetail | undefined): number => {
  if (!assessment?.assessmentDomains) {
    return 0;
  }

  const domains = assessment.assessmentDomains as AssessmentDomainDetail[];
  let totalCount = 0;

  domains.forEach((domain) => {
    if (domain.documents) {
      // Count confirmed and pending documents that are not deleted
      const confirmedCount = domain.documents.filter(
        (doc) => (doc.status === 'confirmed' || doc.status === 'pending') && !doc.deleted,
      ).length;
      totalCount += confirmedCount;
    }
  });

  return totalCount;
};

// =============================================================================
// Assessment Overview Mappers
// =============================================================================

/**
 * Maps a single domain result entry from the API into a flat DomainResultViewModel.
 * Pre-computes the maturity config so components don't need to call getMaturityByValue.
 */
export const mapToDomainResultViewModel = (domain: DomainResultEntry): DomainResultViewModel => {
  const maturityConfig = getMaturityByValue(domain.results.maturity.score, 'score');

  return {
    domainId: domain.assessmentDomain.id,
    domainName: domain.frameworkDomain.name.trim(),
    maturityScore: domain.results.maturity.percentage,
    maturityLevel: maturityConfig.level,
    maturityConfig,
    gaps: domain.results.gaps,
    isSkipped: domain.assessmentDomain.isSkipped,
    isApplicable: domain.assessmentDomain.isApplicable,
    isEvaluated: true,
    weight: domain.frameworkDomain.weight,
    orderIndex: domain.frameworkDomain.orderIndex ?? domain.assessmentDomain.orderIndex ?? 0,
    missingDocumentsCount: domain.missingDocumentsList.length,
    identifiedGaps: domain.results.analysis.identifiedGaps,
    theRisk: domain.results.analysis.theRisk,
    whatIsNeeded: domain.results.analysis.whatIsNeeded,
    improvementPriorities: domain.results.analysis.improvementPriorities,
    documents: domain.documents,
    missingDocumentsList: domain.missingDocumentsList,
    minEstimatedTime: domain.frameworkDomain.minEstimatedTime,
    maxEstimatedTime: domain.frameworkDomain.maxEstimatedTime,
  };
};

const mapToUnevaluatedDomainResultViewModel = (domain: AssessmentDomainDetail): DomainResultViewModel => {
  const maturityConfig = getMaturityByValue(0, 'score');
  return {
    domainId: domain.id,
    domainName: (domain.frameworkDomain?.name ?? '').trim(),
    maturityScore: 0,
    maturityLevel: 0,
    maturityConfig,
    gaps: 0,
    isSkipped: domain.isSkipped,
    isApplicable: domain.isApplicable,
    isEvaluated: false,
    weight: domain.frameworkDomain?.weight ?? 1,
    orderIndex: domain.frameworkDomain?.orderIndex ?? domain.orderIndex ?? 0,
    missingDocumentsCount: domain.missingDocumentCount ?? 0,
    identifiedGaps: [],
    theRisk: null,
    whatIsNeeded: [],
    improvementPriorities: [],
    documents: [],
    missingDocumentsList: [],
    minEstimatedTime: domain.frameworkDomain?.minEstimatedTime ?? 0,
    maxEstimatedTime: domain.frameworkDomain?.maxEstimatedTime ?? 0,
  };
};

/**
 * Maps the top-level AssessmentResultsMeta from the API into a flat OverallResultsViewModel.
 * Pre-computes the maturity config so components don't need to call getMaturityByValue.
 */
export const mapToOverallResultsViewModel = (results: AssessmentResultsMeta): OverallResultsViewModel => {
  const maturityConfig = getMaturityByValue(results.overallResults.maturity.score, 'score');

  return {
    maturityScore: results.overallResults.maturity.percentage,
    maturityLevel: maturityConfig.level,
    maturityConfig,
    maxMaturityLevel: 5,
    totalArticlesAssessed: results.overallResults.articlesAssessed,
    domainsCovered: results.overallResults.domainsCovered,
    documentsAssessed: results.overallResults.documentsAssessed,
    totalGaps: results.overallResults.gaps.totalCount,
    criticalGaps: results.overallResults.gaps.criticalCount,
    missingDocuments: results.overallResults.missingDocuments,
    processedAt: new Date(results.completedAt),
    userEmail: results.user.email,
  };
};

/**
 * Maps the full API response for the AssessmentOverviewPage into a single view model.
 *
 * API → ViewModel mapping:
 *   results.completedAt / results.user              → overallResults.processedAt / userEmail
 *   results.overallResults.maturity                 → overallResults.maturityScore/Level/Config
 *   results.overallResults.articlesAssessed         → overallResults.totalArticlesAssessed
 *   results.overallResults.domainsCovered           → overallResults.domainsCovered
 *   results.overallResults.documentsAssessed        → overallResults.documentsAssessed
 *   results.overallResults.gaps.totalCount          → overallResults.totalGaps
 *   results.overallResults.gaps.criticalCount       → overallResults.criticalGaps
 *   results.overallResults.missingDocuments         → overallResults.missingDocuments
 *   results.domains[].frameworkDomain               → domainResults[].domainName
 *   results.domains[].results.maturity              → domainResults[].maturityScore/Level/Config
 *   results.domains[].results.gaps                  → domainResults[].gaps
 */
export const mapToAssessmentOverviewViewModel = (
  results: AssessmentResultsMeta,
  assessmentTitle: string,
  language: string,
  allAssessmentDomains?: AssessmentDomainDetail[],
): AssessmentOverviewViewModel => {
  const assessedById = new Map(results.domains.map((d) => [d.assessmentDomain.id, d]));

  let domainResults: DomainResultViewModel[];
  if (allAssessmentDomains && allAssessmentDomains.length > 0) {
    domainResults = [...allAssessmentDomains]
      .sort((a, b) => (a.frameworkDomain?.orderIndex ?? 0) - (b.frameworkDomain?.orderIndex ?? 0))
      .map((ad) => {
        const result = assessedById.get(ad.id);
        return result ? mapToDomainResultViewModel(result) : mapToUnevaluatedDomainResultViewModel(ad);
      });
  } else {
    domainResults = results.domains.map(mapToDomainResultViewModel);
  }

  return { assessmentTitle, language, overallResults: mapToOverallResultsViewModel(results), domainResults };
};
